/**
 * SSO provider registry.
 *
 * Single source of truth for which SSO providers are exposed in the login /
 * signup UI. To add a future provider (Okta, GitHub, etc.):
 *   1. Add an entry below.
 *   2. Add the matching backend entry to `PROVIDER_REGISTRY` in
 *      `modules/user_management/services/sso_service.py`.
 *   3. Add the icon + i18n strings.
 *
 * `order` controls button ordering in the SSO list. Apple's HIG strongly
 * prefers Apple-first on iOS but is happy with Apple-anywhere on other
 * platforms; we honor that by sorting differently per platform via
 * `getSortedProviders()`.
 */
import * as React from 'react';

import { AppleIcon } from '../../components/ui/apple-icon';
import { GoogleIcon } from '../../components/ui/google-icon';
import { MicrosoftIcon } from '../../components/ui/microsoft-icon';
import { getClientPlatform } from '../utils/platform';

export type SSOProviderId = 'google' | 'microsoft' | 'apple';

export interface SSOProvider {
  id: SSOProviderId;
  displayName: string; // Used inside the i18n template `sso.signInWith` (no need to translate this — these are brand names)
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /** Default sort priority on non-Apple platforms (lower = earlier). */
  order: number;
  /** True when the provider has a native-app authentication flow we use
   * instead of the web OAuth redirect. Currently only Apple on iOS. */
  supportsNative: boolean;
}

export const SSO_PROVIDERS: Record<SSOProviderId, SSOProvider> = {
  google: { id: 'google', displayName: 'Google', Icon: GoogleIcon, order: 10, supportsNative: false },
  microsoft: { id: 'microsoft', displayName: 'Microsoft', Icon: MicrosoftIcon, order: 20, supportsNative: false },
  apple: { id: 'apple', displayName: 'Apple', Icon: AppleIcon, order: 30, supportsNative: true },
};

/**
 * Return the providers in the order they should render.
 *
 * On iOS, Apple goes first (Apple HIG requirement). Everywhere else, providers
 * follow their declared `order`.
 */
export function getSortedProviders(): SSOProvider[] {
  const list = Object.values(SSO_PROVIDERS);
  if (getClientPlatform() === 'ios') {
    return [...list].sort((a, b) => {
      if (a.id === 'apple') return -1;
      if (b.id === 'apple') return 1;
      return a.order - b.order;
    });
  }
  return [...list].sort((a, b) => a.order - b.order);
}
